import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { switchMap, catchError, takeUntil, filter } from 'rxjs/operators';
import { NeoBaseService } from './base/neo-base.service';
import { NeoConfigAccessLevel } from '@app/models/neo-config';
import { environment } from '../../environments/environment';
import { RedirectRequest } from '@azure/msal-browser';

/**
 * User Profile interface based on JWT token claims
 */
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  givenName?: string;
  familyName?: string;
  displayName?: string;
  roles: string[];
  tenantId?: string;
  isAuthenticated: boolean;
}

/**
 * JWT Token Claims interface for Azure AD B2C
 */
export interface JWTClaims {
  aud?: string; // Audience
  iss?: string; // Issuer
  sub?: string; // Subject (user ID)
  email?: string;
  emails?: string[]; // Azure B2C uses emails array
  name?: string;
  given_name?: string;
  family_name?: string;
  oid?: string; // Object ID

  // Boolean-based role extension attributes (Neo Intelligence B2C)
  extension_NeoSuperAdmin?: boolean;
  extension_NeoAdmin?: boolean;
  extension_NeoUser?: boolean;

  scp?: string; // Scopes
  exp?: number; // Expiration
  iat?: number; // Issued at
  nbf?: number; // Not before
  auth_time?: number; // Authentication time
  tid?: string; // Tenant ID
  tfp?: string; // Trust framework policy
  ver?: string; // Version
  [key: string]: any; // Allow any other claims
}

/**
 * Authentication state interface
 */
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;
  error: string | null;
}

/**
 * Comprehensive Authentication Service
 * Handles JWT parsing, user state management, and role-based permissions
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService extends NeoBaseService {

  private msalService = inject(MsalService);
  private router = inject(Router);

  // Core authentication state using signals
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _user = signal<UserProfile | null>(null);
  private readonly _error = signal<string | null>(null);
  private readonly _accessToken = signal<string | null>(null);

  // Public readonly signals
  public readonly isAuthenticated = this._isAuthenticated.asReadonly();
  public readonly isLoading = this._isLoading.asReadonly();
  public readonly user = this._user.asReadonly();
  public readonly error = this._error.asReadonly();
  public readonly accessToken = this._accessToken.asReadonly();

  // Authentication state computed signal
  public readonly authState = computed<AuthState>(() => ({
    isAuthenticated: this._isAuthenticated(),
    isLoading: this._isLoading(),
    user: this._user(),
    error: this._error()
  }));

  // Role-based permission signals
  public readonly isUser = computed<boolean>(() => {
    const user = this._user();
    if (!user?.isAuthenticated) {
      return false;
    }
    const result = this.hasAnyNormalizedRole(user, [
      NeoConfigAccessLevel.NeoUser,
      NeoConfigAccessLevel.NeoAdmin,
      NeoConfigAccessLevel.NeoSuperAdmin
    ]);
    console.log('🔐 [AUTH] isUser computed:', result, 'User:', user);
    return result;
  });

  public readonly isAdminUser = computed<boolean>(() => {
    const user = this._user();
    const result = this.hasAnyNormalizedRole(user, [
      NeoConfigAccessLevel.NeoAdmin,
      NeoConfigAccessLevel.NeoSuperAdmin
    ]);
    console.log('🔐 [AUTH] isAdminUser computed:', result, 'User:', user);
    return result;
  });

  public readonly isSuperAdminUser = computed<boolean>(() => {
    const user = this._user();
    const result = this.hasAnyNormalizedRole(user, [NeoConfigAccessLevel.NeoSuperAdmin]);
    console.log('🔐 [AUTH] isSuperAdminUser computed:', result, 'User:', user);
    return result;
  });

  // User display information
  public readonly userDisplayName = computed<string>(() => {
    const user = this._user();
    return user?.displayName || user?.name || user?.email || 'User';
  });

  public readonly userEmail = computed<string>(() => {
    const user = this._user();
    return user?.email || '';
  });

  // Legacy observables for backward compatibility
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public readonly token$ = this.tokenSubject.asObservable();

  constructor() {
    super();
    this.initializeAuthService();
    this.setupTokenMonitoring();
  }

  /**
   * Initialize authentication service
   * NOTE: checkAuthenticationState() is NOT called here - it's called by APP_INITIALIZER
   * after MSAL is fully initialized to avoid race conditions.
   */
  private initializeAuthService(): void {
    this.neoLogService.info('Initializing AuthService (MSAL init pending)...');

    // Setup effect to sync token with BehaviorSubject for backward compatibility
    effect(() => {
      const token = this._accessToken();
      this.tokenSubject.next(token);
    });

    // NOTE: Do NOT call checkAuthenticationState() here!
    // MSAL may not be initialized yet. APP_INITIALIZER handles this.
    this.neoLogService.info('AuthService constructor complete');
  }

  /**
   * Check current authentication state
   */
  public checkAuthenticationState(): void {
    try {
      console.log('🔐 [AUTH] Checking authentication state...');
      const accounts = this.msalService.instance.getAllAccounts();
      console.log('🔐 [AUTH] MSAL accounts found:', accounts.length);

      if (accounts.length === 0) {
        console.log('🔐 [AUTH] No accounts found, clearing state');
        this.clearAuthenticationState();
        return;
      }

      const account = accounts[0];
      console.log('🔐 [AUTH] Using account:', account.username);
      this.neoLogService.info('Found authenticated account:', account.username);

      // Extract user profile from account and ID token
      this.extractUserProfile(account);

    } catch (error) {
      console.error('🔐 [AUTH] ❌ Error checking authentication state:', error);
      this.neoLogService.error('Error checking authentication state:', error);
      this._error.set('Failed to check authentication state');
      this.clearAuthenticationState();
    }
  }

  /**
   * Extract user profile from MSAL account and parse JWT claims
   */
  private extractUserProfile(account: any): void {
    try {
      console.log('🔐 [AUTH] Extracting user profile from account:', account.username);
      this.neoLogService.info('Extracting user profile from account:', account.username);

      // Parse ID token claims for user information
      const idTokenClaims = account.idTokenClaims as JWTClaims;
      console.log('🔐 [AUTH] ID Token Claims (full object):', idTokenClaims);
      console.log('🔐 [AUTH] ID Token Claims (stringified):', JSON.stringify(idTokenClaims, null, 2));
      console.log('🔐 [AUTH] All claim keys:', Object.keys(idTokenClaims || {}));
      this.neoLogService.debug('ID Token Claims:', idTokenClaims);

      // Extract roles from JWT claims
      const roles = this.extractRoles(idTokenClaims);

      // Build user profile
      const userProfile: UserProfile = {
        id: idTokenClaims?.sub || account.localAccountId || '',
        email: idTokenClaims?.email || account.username || '',
        name: idTokenClaims?.name || account.name || '',
        givenName: idTokenClaims?.given_name,
        familyName: idTokenClaims?.family_name,
        displayName: idTokenClaims?.name || account.name || account.username,
        roles: roles,
        tenantId: idTokenClaims?.tid,
        isAuthenticated: true
      };

      console.log('🔐 [AUTH] ✅ User profile built successfully:', {
        id: userProfile.id,
        email: userProfile.email,
        name: userProfile.name,
        roles: userProfile.roles,
        isAuthenticated: userProfile.isAuthenticated
      });

      this.neoLogService.info('User profile extracted:', {
        id: userProfile.id,
        email: userProfile.email,
        name: userProfile.name,
        roles: userProfile.roles
      });

      // Update authentication state
      this._user.set(userProfile);
      this._isAuthenticated.set(true);
      this._error.set(null);

      console.log('🔐 [AUTH] State updated - isAuthenticated:', this._isAuthenticated());

      // Get access token
      this.refreshAccessToken();

    } catch (error) {
      console.error('🔐 [AUTH] ❌ Error extracting user profile:', error);
      this.neoLogService.error('Error extracting user profile:', error);
      this._error.set('Failed to extract user profile');
      this.clearAuthenticationState();
    }
  }

  /**
   * Extract roles from JWT claims
   * Neo Intelligence B2C uses boolean extension attributes (extension_NeoSuperAdmin, etc.)
   */
  private extractRoles(claims: JWTClaims | undefined): string[] {
    const roles: string[] = [];

    // Guard against undefined claims (can happen when ID token is not yet loaded)
    if (!claims) {
      console.log('🔐 [AUTH] ⚠️ No claims provided to extractRoles, defaulting to NeoUser');
      return [NeoConfigAccessLevel.NeoUser];
    }

    try {
      console.log('🔐 [AUTH] ==========================================');
      console.log('🔐 [AUTH] Extracting roles from JWT claims...');
      console.log('🔐 [AUTH] ==========================================');

      console.log('🔐 [AUTH] Checking boolean extension attributes...');
      console.log('🔐 [AUTH]   - extension_NeoSuperAdmin:', claims.extension_NeoSuperAdmin);
      console.log('🔐 [AUTH]   - extension_NeoAdmin:', claims.extension_NeoAdmin);
      console.log('🔐 [AUTH]   - extension_NeoUser:', claims.extension_NeoUser);

      if (claims.extension_NeoSuperAdmin === true) {
        roles.push(NeoConfigAccessLevel.NeoSuperAdmin);
        console.log('🔐 [AUTH] ✅ Found extension_NeoSuperAdmin = true');
      }

      if (claims.extension_NeoAdmin === true) {
        roles.push(NeoConfigAccessLevel.NeoAdmin);
        console.log('🔐 [AUTH] ✅ Found extension_NeoAdmin = true');
      }

      if (claims.extension_NeoUser === true) {
        roles.push(NeoConfigAccessLevel.NeoUser);
        console.log('🔐 [AUTH] ✅ Found extension_NeoUser = true');
      }

      // Default role for authenticated users if no roles found
      if (roles.length === 0) {
        roles.push(NeoConfigAccessLevel.NeoUser);
        console.log('🔐 [AUTH] ⚠️ No roles found in claims, defaulting to NeoUser');
      }

      console.log('🔐 [AUTH] ==========================================');
      console.log('🔐 [AUTH] ✅ FINAL ROLES:', roles);
      console.log('🔐 [AUTH] ==========================================');
      this.neoLogService.info('Roles extracted from claims:', roles);

      return roles;

    } catch (error) {
      console.error('🔐 [AUTH] ==========================================');
      console.error('🔐 [AUTH] ❌ Error extracting roles:', error);
      console.error('🔐 [AUTH] Defaulting to neo-user');
      console.error('🔐 [AUTH] ==========================================');
      this.neoLogService.warn('Error extracting roles, defaulting to neo-user:', error);
      return [NeoConfigAccessLevel.NeoUser];
    }
  }

  /**
   * Get valid access token (with automatic refresh)
   */
  public async getValidToken(): Promise<string | null> {
    try {
      this._isLoading.set(true);

      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length === 0) {
        this.neoLogService.warn('No authenticated accounts found');
        this.clearAuthenticationState();
        return null;
      }

      const account = accounts[0];
      this.neoLogService.info('Getting token for account:', account.username);

      // Try multiple scope strategies for B2C token acquisition
      const scopeStrategies = [
        // Strategy 1: API-specific scope from environment configuration
        environment.azureAdB2C.scopes,
        // Strategy 2: Use client ID as scope (common for B2C)
        [environment.azureAdB2C.clientId],
        // Strategy 3: Standard OpenID scopes with offline_access
        ['openid', 'profile', 'offline_access'],
        // Strategy 4: Just OpenID scopes
        ['openid', 'profile']
      ];

      for (let i = 0; i < scopeStrategies.length; i++) {
        try {
          this.neoLogService.info(`Trying token acquisition strategy ${i + 1} with scopes:`, scopeStrategies[i]);

          const response = await this.msalService.instance.acquireTokenSilent({
            scopes: scopeStrategies[i],
            account: account,
          });

          this.neoLogService.info(`Token acquired successfully with strategy ${i + 1}`);
          this.neoLogService.debug('Token preview:', response.accessToken?.substring(0, 50) + '...');

          this._accessToken.set(response.accessToken);
          this._error.set(null);
          return response.accessToken;

        } catch (error) {
          this.neoLogService.warn(`Token acquisition strategy ${i + 1} failed:`, error);
          if (i === scopeStrategies.length - 1) {
            throw error; // Last strategy failed
          }
        }
      }

      return null; // All strategies failed

    } catch (error) {
      this.neoLogService.error('All token acquisition strategies failed:', error);
      this._error.set('Failed to acquire access token');

      // If silent acquisition fails, try interactive
      try {
        this.neoLogService.info('Attempting interactive token acquisition');
        const interactiveRequest: RedirectRequest = {
          scopes: [environment.azureAdB2C.clientId], // Use client ID as scope for B2C
          redirectUri: environment.azureAdB2C.redirectUri,
        };

        await this.msalService.instance.acquireTokenRedirect(interactiveRequest);
        return null; // Will redirect, so no token returned immediately
      } catch (interactiveError) {
        this.neoLogService.error('Failed to acquire token interactively', interactiveError);
        this._error.set('Interactive token acquisition failed');
        return null;
      }
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Refresh access token silently
   */
  private async refreshAccessToken(): Promise<void> {
    try {
      const token = await this.getValidToken();
      if (token) {
        this._accessToken.set(token);
      }
    } catch (error) {
      this.neoLogService.warn('Failed to refresh access token:', error);
    }
  }

  /**
   * Setup automatic token monitoring and refresh
   * Only refreshes token when user is already authenticated (isAuthenticated signal is true)
   */
  private setupTokenMonitoring(): void {
    // Monitor token every 5 minutes
    // Uses filter to only proceed when user is authenticated (state machine has completed)
    interval(300000).pipe(
      filter(() => this._isAuthenticated()),
      switchMap(() => this.checkAndRefreshToken()),
      catchError((error) => {
        this.neoLogService.error('Token monitoring error:', error);
        return [];
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  /**
   * Check and refresh token if needed
   */
  private async checkAndRefreshToken(): Promise<void> {
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length > 0) {
      try {
        await this.refreshAccessToken();
        this.neoLogService.debug('Token checked and refreshed if needed');
      } catch (error) {
        this.neoLogService.error('Failed to refresh token automatically:', error);
        this._error.set('Automatic token refresh failed');
      }
    } else {
      this.clearAuthenticationState();
    }
  }

  /**
   * Initiate login process
   */
  public async login(redirectUri?: string): Promise<void> {
    try {
      this._isLoading.set(true);
      this._error.set(null);

      this.neoLogService.info('Starting Azure AD B2C login process');

      // Must request the API scope here, not just openid/profile — B2C only grants
      // consent for scopes requested during interactive login. Without it here,
      // acquireTokenSilent(environment.azureAdB2C.scopes) later has nothing to renew
      // silently and falls back to an openid/profile-only token, whose audience is
      // this app's own clientId rather than Neo.Link's — causing IDX10214 on the API.
      const loginRequest: RedirectRequest = {
        scopes: [...environment.azureAdB2C.scopes, 'offline_access'],
        redirectUri: redirectUri || environment.azureAdB2C.redirectUri,
      };

      await this.msalService.loginRedirect(loginRequest);
    } catch (error) {
      this.neoLogService.error('Login failed:', error);
      this._error.set('Login failed');
      this._isLoading.set(false);
      throw error;
    }
  }

  /**
   * Initiate logout process
   */
  public async logout(): Promise<void> {
    try {
      this._isLoading.set(true);
      this.neoLogService.info('Starting Azure AD B2C logout process');

      // Clear local state first
      this.clearAuthenticationState();

      await this.msalService.logoutRedirect({
        postLogoutRedirectUri: environment.azureAdB2C.postLogoutRedirectUri,
      });
    } catch (error) {
      this.neoLogService.error('Logout failed:', error);
      this._error.set('Logout failed');
      this._isLoading.set(false);
      throw error;
    }
  }

  /**
   * Clear authentication state
   */
  private clearAuthenticationState(): void {
    this._isAuthenticated.set(false);
    this._user.set(null);
    this._accessToken.set(null);
    this._isLoading.set(false);
    this.neoLogService.info('Authentication state cleared');
  }

  /**
   * Force refresh of user profile and token
   */
  public async refreshUserProfile(): Promise<void> {
    this._isLoading.set(true);
    try {
      this.checkAuthenticationState();
      await this.refreshAccessToken();
    } catch (error) {
      this.neoLogService.error('Failed to refresh user profile:', error);
      this._error.set('Failed to refresh user profile');
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Check if user has specific role
   * Roles are stored as NeoConfigAccessLevel values, but input is normalized for flexibility
   */
  public hasRole(role: string): boolean {
    const user = this._user();
    console.log(`🔐 [AUTH] hasRole("${role}") - User:`, user);
    if (!user?.roles) {
      console.log('🔐 [AUTH] hasRole() - No user or roles, returning false');
      return false;
    }
    const normalizedTarget = this.normalizeRole(role);
    console.log(`🔐 [AUTH] hasRole() - Normalized target: "${normalizedTarget}"`);
    console.log(`🔐 [AUTH] hasRole() - User roles:`, user.roles);
    if (!normalizedTarget) {
      console.log('🔐 [AUTH] hasRole() - Normalized target is null, returning false');
      return false;
    }
    const result = user.roles.includes(normalizedTarget);
    console.log(`🔐 [AUTH] hasRole() - Result: ${result}`);
    return result;
  }

  /**
   * Check if user has any of the specified roles
   * Roles are stored as NeoConfigAccessLevel values, but input is normalized for flexibility
   */
  public hasAnyRole(roles: string[]): boolean {
    const user = this._user();
    console.log(`🔐 [AUTH] hasAnyRole(${JSON.stringify(roles)}) - User:`, user);
    const result = this.hasAnyNormalizedRole(user, roles);
    console.log(`🔐 [AUTH] hasAnyRole() - Result: ${result}`);
    return result;
  }

  /**
   * Internal helper to check if user has any of the specified roles
   * Normalizes input roles to NeoConfigAccessLevel values for comparison
   */
  private hasAnyNormalizedRole(user: UserProfile | null, roles: string[]): boolean {
    if (!user?.roles?.length) {
      console.log('🔐 [AUTH] hasAnyNormalizedRole() - No user or roles, returning false');
      return false;
    }

    console.log('🔐 [AUTH] hasAnyNormalizedRole() - User roles:', user.roles);

    const normalizedTargets = roles
      .map(role => {
        const normalized = this.normalizeRole(role);
        console.log(`🔐 [AUTH] hasAnyNormalizedRole() - Normalizing target: "${role}" -> "${normalized}"`);
        return normalized;
      })
      .filter((role): role is string => !!role);

    console.log('🔐 [AUTH] hasAnyNormalizedRole() - Normalized targets:', normalizedTargets);

    if (!normalizedTargets.length) {
      console.log('🔐 [AUTH] hasAnyNormalizedRole() - No normalized targets, returning false');
      return false;
    }

    const result = normalizedTargets.some(target => {
      const matches = user.roles.includes(target);
      console.log(`🔐 [AUTH] hasAnyNormalizedRole() - Checking "${target}": ${matches}`);
      return matches;
    });

    console.log(`🔐 [AUTH] hasAnyNormalizedRole() - Final result: ${result}`);
    return result;
  }

  private normalizeRole(role: string | null | undefined): string | null {
    if (!role) {
      return null;
    }

    const trimmed = role.toString().trim();
    if (!trimmed) {
      return null;
    }

    const normalized = trimmed
      .replace(/[_\s]+/g, '-')
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase();

    switch (normalized) {
      case 'admin':
      case 'neo-admin':
        return NeoConfigAccessLevel.NeoAdmin;
      case 'super-admin':
      case 'superadmin':
      case 'neo-super-admin':
        return NeoConfigAccessLevel.NeoSuperAdmin;
      case 'user':
      case 'neo-user':
        return NeoConfigAccessLevel.NeoUser;
      default:
        return normalized;
    }
  }

  /**
   * Get token as Observable (for backward compatibility)
   */
  public getToken(): Observable<string | null> {
    return this.token$;
  }

  /**
   * Legacy method for checking authentication (backward compatibility)
   */
  public isUserAuthenticated(): boolean {
    return this._isAuthenticated();
  }

  /**
   * Legacy method for getting display name (backward compatibility)
   */
  public getUserDisplayName(): string {
    return this.userDisplayName();
  }
}
